import React from 'react';
import { FaNodeJs, FaReact, FaAngular, FaDocker, FaDatabase } from 'react-icons/fa';
import { SiTypescript, SiMongodb, SiKubernetes, SiNestjs, SiRedis, SiDevexpress, SiCommonworkflowlanguage, SiPython, SiFlask, SiMysql } from 'react-icons/si';

const Experience = () => {
  // Technology icons mapping
  const techIcons = {
    'Node.js': <FaNodeJs className="text-green-500" />,
    'TypeScript': <SiTypescript className="text-blue-500" />,
    'React': <FaReact className="text-blue-400" />,
    'Angular': <FaAngular className="text-red-500" />,
    'MongoDB': <SiMongodb className="text-green-600" />,
    'NestJS': <SiNestjs className="text-red-600" />,
    'Docker': <FaDocker className="text-blue-400" />,
    'Kubernetes': <SiKubernetes className="text-blue-500" />,
    'Redis': <SiRedis className="text-red-600" />,
    'SQL': <FaDatabase className="text-blue-300" />,
    'ExpressJs': <SiDevexpress className="text-blue-300" />,
    'Workflow Automation': <SiCommonworkflowlanguage className="text-green-600" />,
    'Python': <SiPython className="text-yellow-300" />,
    "Flask": <SiFlask className="text-green-300"/>,
    "MySQL": <SiMysql className="text-blue-400"/>
  };

  const experiences = [
    {
      company: "PwC",
      role: "Software Engineer",
      duration: "July 2023 - Present",
      durationMonths: "2+ year",
      technologies: ["Node.js", "ExpressJs", "TypeScript", "React", "Angular", "MongoDB","MySQL", "Azure", "Kafka", "Docker", "NestJS"],
      points: [
        "Designed and deployed REST APIs (Node.js, Express, MongoDB, TypeScript) for invoice management, reducing approval time by 100% via automated workflows and email notifications.",
        "Connected 3+ internal systems (GFS, ZBV2, Camunda) to microservices, eliminating manual intervention and boosting operational efficiency by 40%.",
        "Streamlined Kafka KSQL pipelines, reducing latency by 80%, and automated deployments using CI/CD (Azure DevOps).",
        "Improved frontend (Angular/React) and backend functionalities for Procurement and Invoicing, increasing user satisfaction by 30%.",
        "Partnered with Product, QA, and DevOps teams to deliver features aligned with business goals."
      ]
    },
    {
      company: "Zluri",
      role: "Software Engineer Intern",
      duration: "Jan 2023 - June 2023",
      durationMonths: "6 months",
      technologies: ["Node.js", "TypeScript", "MongoDB", "Redis", 'Workflow Automation'],
      points: [
        "Unified 50+ applications (Okta, Jira, MS Teams) using Node.js, TypeScript, and MongoDB, enabling centralized management.",
        "Automated processes across 70+ SaaS tools (Slack, PagerDuty, Google Analytics), reducing manual effort by 60%.",
        "Fixed compilation and workflow issues, increasing automation success rates by 80%."
      ]
    },
    {
      company: "Questodev",
      role: "Backend Engineer Intern",
      duration: "Nov 2022 - Jan 2023",
      durationMonths: "3 months",
      technologies: ["Node.js", "React", "MongoDB", "ExpressJs", "WebRTC"],
      points: [
        "Built multitech stack workspace images (Node.js, React, MongoDB, WebRTC), boosting developer efficiency by 40%.",
        "Created RESTful APIs for workspace analytics (resource usage, system details), improving monitoring efficiency by 70%."
      ]
    },
    {
      company: "DevelopKaro",
      role: "SDE Intern",
      duration: "June 2022 - July 2022",
      durationMonths: "2 months",
      technologies: ["Node.js", "ExpressJs", "React", "MongoDB", "WebRTC"],
      points: [
        "Coordinated a team of front-end and back-end developer to facilitate the development process of software’s by strictly following clients need",
        "Developed a robust algorithm and REST API’s for Hotel, Restaurant Booking Management System and an Appraisal System for Satyabama Institute of Technology."
      ]
    },
    {
      company: "GeniusFolk",
      role: "SDE Intern",
      duration: "Feb 2022 - June 2022",
      durationMonths: "5 months",
      technologies: ["React", "MySQL", "Python", "Flask"],
      points: [
        "Designed and developed an Incident Management System(A productive and growth software’s) for Root Cause Analysis of software’s in an enterprise",
        "Coordinated the front-end team and works on frontend in state management, consuming back-end API’s and developed reusable frontend component in ReactJs."
      ]
    }
  ];

  return (
    <section id="experience" className="py-20 px-4 bg-gray-900">
      <div className="container mx-auto max-w-6xl">
        <h2 className="text-3xl font-bold mb-12 text-center">
          <span className="border-b-4 border-blue-400 pb-2">Professional Experience</span>
        </h2>

        <div className="relative">
          {/* Timeline line */}
          <div className="hidden md:block absolute left-1/2 h-full w-1 bg-blue-400 transform -translate-x-1/2"></div>

          {experiences.map((exp, index) => (
            <div
              key={index}
              className={`relative mb-12 ${index % 2 === 0 ? 'md:pr-20 md:pl-0' : 'md:pl-20 md:pr-0'}`}
            >
              <div className={`bg-gray-800 p-6 rounded-lg shadow-lg relative ${index % 2 === 0 ? 'md:mr-auto md:w-5/6' : 'md:ml-auto md:w-5/6'}`}>
                {/* Timeline dot */}
                <div className="hidden md:block absolute top-6 -ml-3 w-6 h-6 rounded-full bg-blue-400 border-4 border-gray-900 transform -translate-x-1/2 left-1/2"></div>

                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-blue-400">{exp.company}</h3>
                    <div className="text-gray-300 font-medium">{exp.role}</div>
                  </div>
                  <div className="mt-2 md:mt-0">
                    <span className="bg-blue-900 bg-opacity-50 text-blue-300 px-3 py-1 rounded-full text-sm">
                      {exp.duration} • {exp.durationMonths}
                    </span>
                  </div>
                </div>

                {/* Technologies used */}
                <div className="flex flex-wrap gap-3 mb-4">
                  {exp.technologies.map((tech, i) => (
                    <div key={i} className="flex items-center bg-gray-700 px-3 py-1 rounded-full text-sm">
                      {techIcons[tech] || <FaDatabase className="text-gray-400 mr-1" />}
                      <span className="ml-1 text-gray-300">{tech}</span>
                    </div>
                  ))}
                </div>

                <ul className="list-disc pl-5 space-y-2 text-gray-300">
                  {exp.points.map((point, i) => (
                    <li key={i} className="text-justify">{point}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;